import { Button, Flex, Input, Skeleton, chakra } from '@chakra-ui/react';
import React from 'react';

import type { PaginationRapParams } from './types';

import { usePaginationRap } from './usePaginationRap';

interface Props extends PaginationRapParams {
  className?: string;
}

// Random Access Pages
const PaginationRap = ({
  page: currentPage,
  totalCount,
  pageSize,
  onTargetPageClick,
  isLoading,
  className,
  onPageInputChange,
  resetPage,
  pageInput,
}: Props) => {

  const paginationRange = usePaginationRap({
    currentPage,
    totalCount,
    pageSize,
  });

  if (!paginationRange) {
    return null;
  }

  if (currentPage === 0 || paginationRange.length < 2) {
    return null;
  }

  const handlePageClick = (page: number) => {
    return () => {
      if (page === 1) {
        resetPage();
      } else {
        onTargetPageClick(page);
      }
    };
  };

  const handleOnDotClick = (index: number) => {
    return () => {
      if (!paginationRange.length) {
        return;
      }
      if (index > (paginationRange.length / 2)) {
        const target = Number(paginationRange[ index - 1 ]);
        if (!isNaN(target)) {
          onTargetPageClick(target + 1);
        }
      } else {
        const target = Number(paginationRange[ index + 1 ]);
        if (!isNaN(target)) {
          onTargetPageClick(target - 1);
        }
      }
    };
  };

  return (
    <Flex
      className={ className }
      fontSize="sm"
      alignItems="center"
    >
      { paginationRange.map((pageNumber, index) => {
        if (pageNumber === '...') {
          return (
            <Skeleton key={ index } isLoaded={ !isLoading } display="inline-block" mr={ 2 } borderRadius="base">
              <Button
                variant="outline"
                size="sm"
                onClick={ handleOnDotClick(index) }
                isDisabled={ isLoading }
              >
                ...
              </Button>
            </Skeleton>
          );
        }
        return (
          <Skeleton key={ index } isLoaded={ !isLoading } display="inline-block" mr={ 2 } borderRadius="base">
            <Button
              key={ index }
              variant="outline"
              size="sm"
              data-selected={ pageNumber === currentPage }
              onClick={ handlePageClick(Number(pageNumber)) }
              isDisabled={ isLoading }
            >
              { pageNumber }
            </Button>
          </Skeleton>
        );
      }) }
      <Flex alignItems="center" width="132px" ml={ 4 } display={{ base: 'none', lg: 'flex' }}>
        Go to <Input w="84px" size="xs" disabled={ isLoading } ml={ 2 } type="number" value={ pageInput } onChange={ onPageInputChange }/>
      </Flex>
    </Flex>
  );
};

export default chakra(PaginationRap);
